package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.Text

/**
 * Primary button component with proper focus and hover states.
 *
 * @param text Button label text
 * @param onClick Click handler
 * @param variant "primary" (solid green) or "secondary" (outlined)
 * @param enabled Whether the button is clickable
 * @param fullWidth Whether button should take full container width
 */
@Composable
fun Button(
    text: String,
    onClick: () -> Unit,
    variant: String = "primary",
    enabled: Boolean = true,
    fullWidth: Boolean = false
) {
    val backgroundColor = when {
        !enabled -> Colors.Gray300
        variant == "secondary" -> Colors.Surface
        else -> Colors.Primary
    }

    val textColor = when {
        !enabled -> Colors.TextDisabled
        variant == "secondary" -> Colors.Primary
        else -> Colors.White
    }

    val borderColor = when {
        !enabled -> Colors.Gray300
        variant == "secondary" -> Colors.Primary
        else -> backgroundColor
    }

    HtmlButton({
        // Add CSS classes for focus/hover states defined in global CSS
        classes("baseline-button")
        if (variant == "primary") {
            classes("baseline-button--primary")
        } else {
            classes("baseline-button--secondary")
        }

        onClick { if (enabled) onClick() }
        if (!enabled) {
            attr("disabled", "true")
        }
        style {
            backgroundColor(backgroundColor)
            color(textColor)
            border(1.px, LineStyle.Solid, borderColor)
            borderRadius(8.px)
            padding(12.px, 24.px)
            fontSize(16.px)
            fontWeight(600)
            minHeight(44.px) // Minimum touch target for accessibility
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            cursor(if (enabled) "pointer" else "not-allowed")
            property("transition", "all 0.2s")
            if (fullWidth) {
                width(100.percent)
            }
        }
    }) {
        Text(text)
    }
}
